import { useState, useEffect } from 'react';

import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import TextField from '@mui/material/TextField';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';

import { warehouses, suppliers } from '../../services/dataClient';
import '../../styles/design-tokens.css';
import { fieldSx } from '../../theme/fieldStyles';

// ─── Constants ────────────────────────────────────────────────────────────────

const DISPOSITION_OPTIONS = [
  { value: 'Restock',            label: 'Restock',            resultStatus: 'Restocked'           },
  { value: 'Refurbish',          label: 'Refurbish',          resultStatus: 'Inspecting'           },
  { value: 'Return to Supplier', label: 'Return to Supplier', resultStatus: 'Returned to Supplier' },
  { value: 'Scrap',              label: 'Scrap',              resultStatus: 'Scrapped'             },
];

const activeWarehouses = warehouses.filter((w) => w.active && w.type !== 'On-Site');
const activeSuppliers  = suppliers.filter((s) => s.active);

const EMPTY_FORM = {
  disposition:     '',
  inspectionNotes: '',
  warehouseId:     '',
  supplierId:      '',
};

/**
 * @param {{
 *   open: boolean,
 *   rma: object | null,
 *   onClose: () => void,
 *   onSave: (result: { disposition: string, resultStatus: string, inspectionNotes: string, warehouseId?: string, supplierId?: string }) => void,
 * }} props
 */
export default function DispositionModal({ open, rma, onClose, onSave }) {
  const [form, setForm] = useState(EMPTY_FORM);

  useEffect(() => {
    if (!open) return;
    setForm({
      ...EMPTY_FORM,
      // Pre-fill inspection notes if already set
      inspectionNotes: rma?.inspectionNotes ?? '',
    });
  }, [open, rma]);

  const set = (field) => (e) => setForm((prev) => ({ ...prev, [field]: e.target.value }));

  const selectedOption = DISPOSITION_OPTIONS.find((d) => d.value === form.disposition) ?? null;

  const isValid =
    Boolean(form.disposition) &&
    Boolean(form.inspectionNotes.trim()) &&
    (form.disposition !== 'Restock'            || Boolean(form.warehouseId)) &&
    (form.disposition !== 'Return to Supplier' || Boolean(form.supplierId));

  const handleSave = () => {
    if (!isValid || !selectedOption) return;
    onSave({
      disposition:     form.disposition,
      resultStatus:    selectedOption.resultStatus,
      inspectionNotes: form.inspectionNotes.trim(),
      warehouseId:     form.warehouseId  || undefined,
      supplierId:      form.supplierId   || undefined,
    });
  };

  return (
    <Dialog
      open={open}
      onClose={(e, reason) => { if (reason !== 'backdropClick') onClose(); }}
      maxWidth="sm"
      fullWidth
    >
      {/* ── Title row ────────────────────────────────────────────────────── */}
      <DialogTitle sx={{ fontWeight: 600,
        fontSize: '1rem',
        color: 'var(--grey-900)',
        pb: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
      }}>
        Record Disposition
        {rma && (
          <Typography component="span" sx={{ fontSize: '0.875rem', color: 'var(--grey-500)', fontWeight: 400 }}>
            {rma.rmaNumber}
          </Typography>
        )}
        <IconButton
          size="small"
          onClick={onClose}
          sx={{ color: 'var(--grey-400)', '&:hover': { color: 'var(--grey-700)' }, mr: -1 }}
        >
          <HighlightOffIcon />
        </IconButton>
      </DialogTitle>

      {/* ── Body ─────────────────────────────────────────────────────────── */}
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '12px !important' }}>

        {/* RMA summary */}
        {rma && (
          <Box sx={{ backgroundColor: 'var(--grey-50)',
            border: '1px solid var(--grey-200)',
            borderRadius: '6px',
            px: 2,
            py: '10px',
            display: 'flex',
            gap: 3,
          }}>
            {[
              { label: 'Part #',    value: rma.partNumber    },
              { label: 'Ticket #',  value: rma.ticketNumber  },
              { label: 'Customer',  value: rma.customerName  },
              { label: 'Reason',    value: rma.returnReason  },
            ].map(({ label, value }) => (
              <Box key={label}>
                <Typography sx={{ fontSize: '0.6875rem', color: 'var(--grey-500)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  {label}
                </Typography>
                <Typography sx={{ fontSize: '0.8125rem', color: 'var(--grey-800)', fontWeight: 500 }}>
                  {value}
                </Typography>
              </Box>
            ))}
          </Box>
        )}

        {/* Disposition select */}
        <FormControl size="small" fullWidth sx={fieldSx}>
          <InputLabel>Disposition</InputLabel>
          <Select
            value={form.disposition}
            onChange={(e) => setForm((prev) => ({
              ...prev,
              disposition: e.target.value,
              warehouseId: '',
              supplierId:  '',
            }))}
            label="Disposition"
          >
            {DISPOSITION_OPTIONS.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                <Box>
                  <Typography sx={{ fontSize: '0.875rem' }}>{opt.label}</Typography>
                  <Typography sx={{ fontSize: '0.75rem', color: 'var(--grey-500)' }}>
                    → sets status to <strong>{opt.resultStatus}</strong>
                  </Typography>
                </Box>
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Conditional: Warehouse selector (Restock only) */}
        {form.disposition === 'Restock' && (
          <FormControl size="small" fullWidth sx={fieldSx}>
            <InputLabel>Restock to Warehouse</InputLabel>
            <Select value={form.warehouseId} onChange={set('warehouseId')} label="Restock to Warehouse">
              {activeWarehouses.map((w) => (
                <MenuItem key={w.id} value={w.id}>{w.name} ({w.type})</MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        {/* Conditional: Supplier selector (Return to Supplier only) */}
        {form.disposition === 'Return to Supplier' && (
          <FormControl size="small" fullWidth sx={fieldSx}>
            <InputLabel>Return to Supplier</InputLabel>
            <Select value={form.supplierId} onChange={set('supplierId')} label="Return to Supplier">
              {activeSuppliers.map((s) => (
                <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        {/* Inspection Notes (required) */}
        <TextField
          size="small"
          label="Inspection Notes *"
          multiline
          rows={4}
          value={form.inspectionNotes}
          onChange={set('inspectionNotes')}
          fullWidth
          sx={fieldSx}
          helperText="Required — describe findings and rationale for the chosen disposition."
          FormHelperTextProps={{ sx: { fontSize: '0.75rem', color: 'var(--grey-500)', ml: 0 } }}
        />

      </DialogContent>

      {/* ── Footer ───────────────────────────────────────────────────────── */}
      <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
        <Typography
          onClick={onClose}
          sx={{ fontSize: '0.875rem',
            color: 'var(--grey-500)',
            cursor: 'pointer',
            '&:hover': { color: 'var(--grey-700)' },
          }}
        >
          Cancel
        </Typography>
        <Button
          variant="contained"
          size="small"
          disabled={!isValid}
          onClick={handleSave}
          sx={{ backgroundColor: '#009688',
            '&:hover': { backgroundColor: '#00796B' },
            '&.Mui-disabled': { backgroundColor: 'var(--grey-200)', color: 'var(--grey-500)' },
            textTransform: 'none',
            fontWeight: 600,
            fontSize: '0.875rem',
            boxShadow: 'none',
            px: 2,
          }}
        >
          Save Disposition
        </Button>
      </DialogActions>
    </Dialog>
  );
}
